import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { EmailTemplates } from "../../../../core/models/orders/emailTemplates";
import { CommonModule } from "@angular/common";
import { SelectModule } from "primeng/select";
import { DisplayPdfFrameService } from "../../../../core/services/display-pdf-frame.service";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";

@Component({
  selector: "jni-po-email-header",
  imports: [
    CommonModule,
    SelectModule,
    ReactiveFormsModule,
    ButtonModule,
    CardModule,
    InputTextModule,
  ],
  templateUrl: "./po-email-header.component.html",
  styleUrls: ["./po-email-header.component.scss"],
})
export class PoEmailHeaderComponent implements OnInit {
  @Input() parent!: FormGroup;
  @Input() templates: EmailTemplates[] = [];
  @Input() poNumber: string[] = [];
  @Input() formErrors!: Record<string, string>;
  @Input() validationMessages!: Record<string, Record<string, string>>;
  @Input() emailButtonStatus = false;
  @Input() poNumberRevision: string[] = [];
  @Output() fetchVendorInformation = new EventEmitter();
  @Output() templateSelectedEvent = new EventEmitter();
  @Output() logValidationErrorsEvent = new EventEmitter();
  @Output() sendEmailEvent = new EventEmitter();
  @Output() fetchWatermarkedPdf = new EventEmitter<{
    rush: boolean;
    revised: boolean;
  }>();

  base64 = "";
  rush = "Rush";
  revised = "Revised";
  rushRevised = "Rush/Revised";
  rushCheck = false;
  revisedCheck = false;
  additionalSubject = "";

  constructor(private displayPdfFrameService: DisplayPdfFrameService) {}
  ngOnInit(): void {
    this.onPONumber();
    this.onRush();
    this.onRevised();
    this.onTemplate();
  }

  onRush() {
    this.displayPdfFrameService.selectedRush$.subscribe(value => {
      this.onRushSelection(value);
    });
  }

  onRevised() {
    this.displayPdfFrameService.selectedRevised$.subscribe(value => {
      this.onRevisedSelection(value);
    });
  }

  onTemplate() {
    this.displayPdfFrameService.selectedtemplates$.subscribe(() => {
      this.onTemplateSelection();
    });
  }

  onPONumber() {
    this.displayPdfFrameService.selectedpoNumber$.subscribe(() => {
      this.onPONumberSelection();
    });
  }

  logValidationErrors() {
    this.logValidationErrorsEvent.emit();
  }

  onPONumberSelection() {
    this.parent.patchValue({
      header: {
        subject: `Nadel PO - ${
          this.parent.get(["header", "poNumber"])?.value || ""
        }`,
      },
    });
    this.fetchVendorInformation.emit(
      this.parent.get(["header", "poNumber"])?.value || ""
    );

    const poNumber = this.parent.get(["header", "poNumber"])?.value;
    if (poNumber) {
      this.rushRevisedFieldEnable();
      this.onRushSelection(null);
      this.onRevisedSelection(null);
    } else {
      this.rushRevisedFieldDisabled();
      this.clearRushRevisedField();
    }
  }

  rushRevisedFieldEnable() {
    this.parent.get(["header", "rush"])?.enable();
    this.parent.get(["header", "revised"])?.enable();
    this.parent.get(["header", "additionalSubject"])?.enable();
  }

  rushRevisedFieldDisabled() {
    this.parent.get(["header", "rush"])?.disable();
    this.parent.get(["header", "revised"])?.disable();
    this.parent.get(["header", "additionalSubject"])?.disable();
  }

  clearRushRevisedField() {
    this.parent.patchValue({
      header: {
        rush: false,
        revised: false,
        additionalSubject: "",
      },
    });
    this.rushCheck = false;
    this.revisedCheck = false;
    this.additionalSubject = "";
  }

  onTemplateSelection() {
    this.templateSelectedEvent.emit(
      this.parent.get(["header", "templateId"])?.value || 0
    );
  }

  onRushSelection(event: Event | null) {
    if (event) {
      const eventTarget = event.target as HTMLInputElement;
      this.rushCheck = eventTarget.checked;
    }
    if (this.rushCheck && this.revisedCheck) {
      this.patchValueInSubject(this.rushRevised, this.additionalSubject);
    } else {
      if (this.rushCheck && !this.revisedCheck) {
        this.patchValueInSubject(this.rush, this.additionalSubject);
      } else if (!this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.revised, this.additionalSubject);
      } else if (!this.rushCheck && !this.revisedCheck) {
        this.patchValueInSubject("", this.additionalSubject);
      }
    }
    this.addPdfWaterMark();
  }

  onRevisedSelection(event: Event | null) {
    if (event) {
      const eventTarget = event.target as HTMLInputElement;
      this.revisedCheck = eventTarget?.checked;
    }
    if (this.rushCheck && this.revisedCheck) {
      this.patchValueInSubject(this.rushRevised, this.additionalSubject);
    } else {
      if (this.rushCheck && !this.revisedCheck) {
        this.patchValueInSubject(this.rush, this.additionalSubject);
      } else if (!this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.revised, this.additionalSubject);
      } else if (!this.rushCheck && !this.revisedCheck) {
        this.patchValueInSubject("", this.additionalSubject);
      }
    }

    this.addPdfWaterMark();
  }

  addPdfWaterMark() {
    this.fetchWatermarkedPdf.emit({
      rush: this.rushCheck,
      revised: this.revisedCheck,
    });
  }

  patchValueInSubject(value: string, additionalSubject: string) {
    if (value && !additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "poNumber"])?.value || ""
          } - ${value}`,
        },
      });
    } else if (value && additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "poNumber"])?.value || ""
          } - ${value} - ${additionalSubject}`,
        },
      });
    } else if (!value && additionalSubject) {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "poNumber"])?.value || ""
          } - ${additionalSubject}`,
        },
      });
    } else {
      this.parent.patchValue({
        header: {
          subject: `Nadel PO - ${
            this.parent.get(["header", "poNumber"])?.value || ""
          }`,
        },
      });
    }
  }

  onAdditionalSubject(event: Event) {
    const eventTarget = event.target as HTMLInputElement;
    this.additionalSubject = eventTarget.value;
    if (this.additionalSubject) {
      if (this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.rushRevised, this.additionalSubject);
      } else {
        if (this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject(this.rush, this.additionalSubject);
        } else if (!this.rushCheck && this.revisedCheck) {
          this.patchValueInSubject(this.revised, this.additionalSubject);
        } else if (!this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject("", this.additionalSubject);
        }
      }
    } else {
      this.additionalSubject = "";
      if (this.rushCheck && this.revisedCheck) {
        this.patchValueInSubject(this.rushRevised, this.additionalSubject);
      } else {
        if (this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject(this.rush, this.additionalSubject);
        } else if (!this.rushCheck && this.revisedCheck) {
          this.patchValueInSubject(this.revised, this.additionalSubject);
        } else if (!this.rushCheck && !this.revisedCheck) {
          this.patchValueInSubject("", this.additionalSubject);
        }
      }
    }
  }

  onEmailPO() {
    this.sendEmailEvent.emit();
  }
}
